import { Clipboard } from "@angular/cdk/clipboard";
import { NgOptimizedImage } from "@angular/common";
import { Component, computed, inject, input, signal, VERSION } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SwUpdate } from "@angular/service-worker";

/** An extra card of label/value rows, e.g. the signed-in user's access. Included in Copy details. */
export interface AboutSection {
  title: string;
  items: { label: string; value: string }[];
}

type UpdateStatus = "idle" | "checking" | "current" | "ready" | "error";

const ENVIRONMENT_LABELS: Record<string, string> = {
  production: "Production",
  staging: "Staging",
  development: "Development",
};

/** Versions are CalVer YYYY.N.PATCH (e.g. 2026.12.0 is the 12th release of 2026), with a -dev suffix locally. */
export function describeVersion(version: string): string | null {
  const match = /^(\d{4})\.(\d+)\.(\d+)/.exec(version);
  if (!match) return null;
  const [, year, release, patch] = match;
  return `Release ${release} of ${year}${patch === "0" ? "" : `, patch ${patch}`}`;
}

/**
 * The About page body shared by the JTMS apps: what the app is, which version/environment/release is
 * running, an update check (when the app has a service worker) and a Copy details button for
 * support requests. Each app wraps it with its own toolbar and passes its environment values.
 */
@Component({
  selector: "lib-about-page",
  templateUrl: "./about-page.component.html",
  styleUrl: "./about-page.component.scss",
  imports: [MatButtonModule, MatCardModule, MatIconModule, NgOptimizedImage],
})
export class AboutPage {
  readonly appName = input.required<string>();
  readonly description = input.required<string>();
  readonly version = input.required<string>();
  /** The environment's `name` (production, staging or development). */
  readonly environment = input.required<string>();
  /** The Sentry release name, so it can be matched to issues in Sentry. */
  readonly release = input.required<string>();
  readonly firebaseProject = input<string>();
  readonly logoSrc = input<string>();
  readonly sections = input<AboutSection[]>([]);

  // Only apps that call provideServiceWorker have SwUpdate, and it's disabled outside production builds.
  #swUpdate = inject(SwUpdate, { optional: true });
  #clipboard = inject(Clipboard);
  #snackBar = inject(MatSnackBar);

  readonly updatesEnabled = !!this.#swUpdate?.isEnabled;
  readonly hasServiceWorker = !!this.#swUpdate;
  readonly angularVersion = VERSION.full;
  readonly versionDescription = computed(() => describeVersion(this.version()));
  readonly environmentLabel = computed(() => ENVIRONMENT_LABELS[this.environment()] ?? this.environment());
  readonly isProduction = computed(() => this.environment() === "production");
  readonly updateStatus = signal<UpdateStatus>("idle");

  async checkForUpdate(): Promise<void> {
    if (!this.#swUpdate) return;
    this.updateStatus.set("checking");
    try {
      // Resolves true once a newer version has been downloaded and is ready to use.
      this.updateStatus.set((await this.#swUpdate.checkForUpdate()) ? "ready" : "current");
    } catch (error) {
      console.error("About: update check failed", error);
      this.updateStatus.set("error");
    }
  }

  reload(): void {
    document.location.reload();
  }

  /** Everything support needs to identify this build and session, as plain text. */
  copyDetails(): void {
    const lines = [
      `${this.appName()} ${this.version()} (${this.environmentLabel()})`,
      `Release: ${this.release()}`,
      ...(this.firebaseProject() ? [`Firebase project: ${this.firebaseProject()}`] : []),
      `Angular: ${this.angularVersion}`,
      ...this.sections().flatMap((section) => section.items.map((item) => `${item.label}: ${item.value}`)),
      `Page: ${document.location.href}`,
      `Browser: ${navigator.userAgent}`,
      `Copied: ${new Date().toISOString()}`,
    ];
    const copied = this.#clipboard.copy(lines.join("\n"));
    this.#snackBar.open(copied ? "Copied version details" : "Couldn't copy to the clipboard", "Dismiss", {
      duration: 3000,
    });
  }
}
